import { type MultiSelectInputWidgetProps, WidgetType } from '@fruitful/types';

import type { ChatMessage } from '../types/ChatTypes';

export interface ActiveMultiSelectWidget {
    messageId: string;
    props: MultiSelectInputWidgetProps;
}

const isMultiSelectWidgetProps = (
    props: Record<string, unknown> | null,
): props is MultiSelectInputWidgetProps & Record<string, unknown> => {
    if (props == null) {
        return false;
    }

    return (
        Array.isArray(props.options) &&
        typeof props.responseKey === 'string' &&
        props.options.every(
            (option) =>
                typeof option === 'object' &&
                option != null &&
                'id' in option &&
                'label' in option,
        )
    );
};

export const getActiveMultiSelectWidget = (
    messages: ChatMessage[],
): ActiveMultiSelectWidget | null => {
    for (let index = messages.length - 1; index >= 0; index -= 1) {
        const message = messages[index];
        if (message?.role !== 'assistant') {
            continue;
        }

        const uiContent = message.content.find(
            (content) =>
                content.type === 'ui' &&
                content.widget === WidgetType.MultiSelectInput,
        );

        if (
            uiContent?.type !== 'ui' ||
            !isMultiSelectWidgetProps(uiContent.props)
        ) {
            continue;
        }

        const hasUserReplyAfterWidget = messages
            .slice(index + 1)
            .some((reply) => reply.role === 'user');

        if (hasUserReplyAfterWidget) {
            return null;
        }

        return {
            messageId: message.id,
            props: uiContent.props,
        };
    }

    return null;
};

export const hasActiveMultiSelectWidget = (messages: ChatMessage[]): boolean =>
    getActiveMultiSelectWidget(messages) != null;

export const isActiveMultiSelectWidget = (
    messageId: string,
    messages: ChatMessage[],
): boolean => getActiveMultiSelectWidget(messages)?.messageId === messageId;
